import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Container,
    Tabs,
    Tab,
    Paper,
    Grid,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    CircularProgress,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Avatar,
    TablePagination,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import GroupAddIcon from '@mui/icons-material/GroupAdd'; // Icon for empty state
import PeopleIcon from '@mui/icons-material/People'; // Icon for Total Children
import HealingIcon from '@mui/icons-material/Healing'; // Icon for Active Therapies
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Icon for Completed Reports
import { useTranslation } from 'react-i18next';

const DoctorDashboard = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(() => {
        const savedTab = localStorage.getItem('dashboardTab');
        if (savedTab) {
            localStorage.removeItem('dashboardTab');
            return parseInt(savedTab, 10);
        }
        return 0;
    });
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTherapies, setActiveTherapies] = useState(0);
    const [completedReports, setCompletedReports] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [deleteChildOpen, setDeleteChildOpen] = useState(false);
    const [childToDelete, setChildToDelete] = useState(null);

    const handleDeleteChildClick = (child) => {
        setChildToDelete(child);
        setDeleteChildOpen(true);
    };

    const confirmDeleteChild = async () => {
        try {
            await axios.post(`${API_BASE_URL}/delete_child.php`, { child_id: childToDelete.id });
            setDeleteChildOpen(false);
            setChildren(children.filter(c => c.id !== childToDelete.id));
        } catch (err) {
            console.error("Failed to delete child:", err);
        }
    };

    const userRole = localStorage.getItem('userRole') || 'doctor';
    const roleTitle = userRole === 'admin' ? "Admin" : userRole.charAt(0).toUpperCase() + userRole.slice(1);
    const dashboardTitle = `${roleTitle} Therapist Dashboard`;
    const userName = userRole === 'admin' ? "DR. PAVAN" : `Therapist (${roleTitle})`;

    // --- MODIFIED SECTION: useEffect for data fetching ---
    useEffect(() => {
        const fetchChildren = async () => {
            setLoading(true);
            try {
                const role = localStorage.getItem('userRole');
                const response = await axios.get(`${API_BASE_URL}/get_children.php?therapist_role=${role}`);
                if (Array.isArray(response.data)) {
                    setChildren(response.data);
                } else {
                    console.error("Expected array but got:", response.data);
                    setChildren([]);
                }
            } catch (error) {
                console.error("Failed to fetch children:", error);
            } finally {
                setLoading(false);
            }
        };

        const fetchStats = async () => {
            try {
                const role = localStorage.getItem('userRole');
                const activeResponse = await axios.get(`${API_BASE_URL}/get_active_therapies_count.php?therapist_role=${role}`);
                setActiveTherapies(activeResponse.data.count);
                const reportsResponse = await axios.get(`${API_BASE_URL}/get_completed_reports_count.php?therapist_role=${role}`);
                setCompletedReports(reportsResponse.data.count);
            } catch (error) {
                console.error("Failed to fetch stats:", error);
            }
        };

        fetchChildren();
        fetchStats();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        navigate('/login');
    };

    const handleTabChange = (event, newValue) => {
        if (newValue === 1) {
            navigate('/register-child');
        } else {
            setTabValue(newValue);
        }
    };

    const changeLanguage = (event) => {
        i18n.changeLanguage(event.target.value);
    };

    // Filtered children based on search term
    const filteredChildren = Array.isArray(children) ? children.filter(child =>
        (child.full_name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (child.parent_name?.toLowerCase() || "").includes(searchTerm.toLowerCase())
    ) : [];

    // Paginated children
    const paginatedChildren = filteredChildren.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const StatisticCard = ({ title, value, description, icon, iconBgColor }) => (
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', width: '100%' }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                </Box>
                <Box sx={{ bgcolor: iconBgColor, borderRadius: '50%', p: 1, display: 'flex' }}>
                    {icon}
                </Box>
            </Paper>
        </Grid>
    );

    const ManageTherapists = () => {
        const [therapistData, setTherapistData] = useState({ username: '', password: '', full_name: '', role: 'behavioral' });
        const [therapists, setTherapists] = useState([]);
        const [loading, setLoading] = useState(true);
        const [status, setStatus] = useState('');

        const [deleteTherapistOpen, setDeleteTherapistOpen] = useState(false);
        const [therapistToDelete, setTherapistToDelete] = useState(null);

        const handleDeleteTherapistClick = (therapist) => {
            setTherapistToDelete(therapist);
            setDeleteTherapistOpen(true);
        };

        const confirmDeleteTherapist = async () => {
            try {
                await axios.post(`${API_BASE_URL}/delete_therapist.php`, { therapist_id: therapistToDelete.id });
                setDeleteTherapistOpen(false);
                fetchTherapists();
            } catch (err) {
                console.error("Failed to delete therapist:", err);
            }
        };

        const fetchTherapists = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${API_BASE_URL}/get_therapists.php`);
                setTherapists(response.data);
            } catch (error) {
                console.error("Failed to fetch therapists:", error);
            } finally {
                setLoading(false);
            }
        };

        useEffect(() => {
            fetchTherapists();
        }, []);

        const handleAddTherapist = async (e) => {
            e.preventDefault();
            try {
                await axios.post(`${API_BASE_URL}/add_therapist.php`, therapistData);
                setStatus('Therapist added successfully!');
                setTherapistData({ username: '', password: '', full_name: '', role: 'behavioral' });
                fetchTherapists();
            } catch (err) {
                setStatus('Failed to add therapist.');
            }
        };

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>Add New Therapist</Typography>
                    <Box component="form" onSubmit={handleAddTherapist} sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 400 }}>
                        <TextField label="Full Name" value={therapistData.full_name} onChange={e => setTherapistData({ ...therapistData, full_name: e.target.value })} onClick={(e) => e.stopPropagation()} fullWidth required />
                        <TextField label="Email / Username" value={therapistData.username} onChange={e => setTherapistData({ ...therapistData, username: e.target.value })} fullWidth required />
                        <TextField label="Password" type="password" value={therapistData.password} onChange={e => setTherapistData({ ...therapistData, password: e.target.value })} fullWidth required />
                        <FormControl fullWidth>
                            <Select value={therapistData.role} onChange={e => setTherapistData({ ...therapistData, role: e.target.value })} required>
                                <MenuItem value="behavioral">Behavioral Therapist</MenuItem>
                                <MenuItem value="occupational">Occupational Therapist</MenuItem>
                                <MenuItem value="speech">Speech Therapist</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                        <Button type="submit" variant="contained" sx={{ bgcolor: '#00bcd4', py: 1.5 }}>Create Account</Button>
                        {status && <Typography color={status.includes('success') ? 'success.main' : 'error.main'}>{status}</Typography>}
                    </Box>
                </Paper>

                <Paper elevation={0} sx={{ p: 4, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>Registered Therapists</Typography>
                    {loading ? <CircularProgress /> : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Username / Email</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {therapists.map((t) => (
                                        <TableRow key={t.id}>
                                            <TableCell>{t.full_name}</TableCell>
                                            <TableCell>{t.username}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{t.role}</TableCell>
                                            <TableCell>
                                                <IconButton color="error" onClick={() => handleDeleteTherapistClick(t)}><DeleteIcon /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Delete Therapist Dialog */}
                    <Dialog open={deleteTherapistOpen} onClose={() => setDeleteTherapistOpen(false)}>
                        <DialogTitle>Delete Therapist</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Are you sure you want to delete the therapist <strong>{therapistToDelete?.full_name}</strong>? This action cannot be undone.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteTherapistOpen(false)}>Cancel</Button>
                            <Button color="error" onClick={confirmDeleteTherapist} variant="contained">Delete</Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', overflowX: 'hidden', bgcolor: 'white' }}>
            {/* Main Header */}
            <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: 'white' }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ bgcolor: '#00bcd4', borderRadius: '8px', p: 0.5, display: 'flex' }}>
                            <FavoriteIcon sx={{ color: 'white', fontSize: '20px' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('akidscare_brand')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LanguageIcon sx={{ color: 'text.secondary' }} />
                            <FormControl variant="standard" sx={{ minWidth: 60 }}>
                                <Select value={i18n.language} onChange={changeLanguage} disableUnderline>
                                    <MenuItem value="en">EN</MenuItem>
                                    <MenuItem value="te">TE</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>{userName} ({userRole})</Typography>
                        <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('logout_button')}</Box>
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Navigation Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                        <Tab
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SettingsIcon /><Typography>{userRole === 'admin' ? "Admin Dashboard" : `${roleTitle} Dashboard`}</Typography></Box>}
                            sx={{ textTransform: 'none' }}
                        />
                        <Tab label="Register Child" sx={{ textTransform: 'none' }} />
                        {userRole === 'admin' && <Tab label="Manage Therapists" sx={{ textTransform: 'none' }} />}
                    </Tabs>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
                {tabValue === 0 && (
                    <>
                        {/* Page Title & Actions */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4, width: '100%' }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                    {userRole === 'admin' ? t('admin_dashboard_title') : `${roleTitle} Therapist Dashboard`}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">{t('welcome_back_doctor')}</Typography>
                            </Box>
                        </Box>

                        {/* Statistics Cards */}
                        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
                            <StatisticCard
                                title={t('total_children_card')}
                                value={children.length}
                                description="Registered in the system"
                                icon={<PeopleIcon sx={{ color: 'white' }} />}
                                iconBgColor="#4fc3f7"
                            />
                            <StatisticCard title={t('active_therapies_card')} value={activeTherapies} description="Currently ongoing" icon={<HealingIcon sx={{ color: 'white' }} />} iconBgColor="#81c784" />
                            <StatisticCard title={t('completed_reports_card')} value={completedReports} description="Reports generated" icon={<CheckCircleIcon sx={{ color: 'white' }} />} iconBgColor="#ba68c8" />
                        </Grid>

                        <Paper elevation={0} sx={{ p: 3, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('children_management_panel_title')}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                                    <TextField fullWidth variant="outlined" size="small" placeholder={t('search_child_placeholder')} value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>), sx: { borderRadius: '8px' } }} />
                                    <Button 
                                        variant="contained" 
                                        sx={{ 
                                            bgcolor: '#00bcd4', 
                                            whiteSpace: 'nowrap',
                                            display: { xs: 'none', sm: 'flex' },
                                            '&:hover': { bgcolor: '#00acc1' } 
                                        }} 
                                        startIcon={<AddIcon />} 
                                        component={Link} 
                                        to="/register-child"
                                    >
                                        Add Child
                                    </Button>
                                </Box>
                                {/* Mobile Add Button */}
                                <Button 
                                    variant="contained" 
                                    fullWidth
                                    sx={{ 
                                        bgcolor: '#00bcd4', 
                                        display: { xs: 'flex', sm: 'none' },
                                        '&:hover': { bgcolor: '#00acc1' } 
                                      }} 
                                    startIcon={<AddIcon />} 
                                    component={Link} 
                                    to="/register-child"
                                >
                                    Add Child
                                </Button>
                            </Box>

                            {/* --- MODIFIED SECTION: Conditional Rendering for Loading, Empty, and Data states --- */}
                            {loading ? (
                                <Box sx={{ textAlign: 'center', py: 5 }}>
                                    <CircularProgress />
                                </Box>
                            ) : filteredChildren.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 5 }}>
                                    <Box sx={{ bgcolor: '#f0f0f0', borderRadius: '50%', width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                                        <GroupAddIcon sx={{ fontSize: 60, color: '#bdbdbd' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{t('no_children_registered_yet_title')}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '400px', mx: 'auto' }}>{t('no_children_registered_yet_description')}</Typography>
                                    <Button variant="contained" sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#00acc1' } }} startIcon={<AddIcon />} component={Link} to="/register-child">
                                        {t('register_first_child_button')}
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ width: '100%' }}>
                                    {/* Mobile Card Layout */}
                                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                        {paginatedChildren.map((child) => (
                                            <Card key={child.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                                        <Avatar src={child.photo_url || ''} />
                                                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{child.full_name}</Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                        <strong>Parent:</strong> {child.parent_name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                        <strong>DOB:</strong> {new Date(child.date_of_birth).toLocaleDateString()}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                        <strong>Gender:</strong> {child.gender}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                        <strong>Joining Date:</strong> {child.joining_date ? new Date(child.joining_date).toLocaleDateString() : 'N/A'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                                        <Button component={Link} to={`/child-profile/${child.id}`} variant="outlined" size="small" fullWidth>
                                                            Profile
                                                        </Button>
                                                        {userRole === 'admin' && (
                                                            <Button color="error" variant="outlined" size="small" fullWidth onClick={() => handleDeleteChildClick(child)}>
                                                                Delete
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>

                                    {/* Desktop Table Layout */}
                                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Child Name</TableCell>
                                                        <TableCell>Parent Name</TableCell>
                                                        <TableCell>Joining Date</TableCell>
                                                        <TableCell>Therapies</TableCell>
                                                        <TableCell>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedChildren.map((child) => (
                                                        <TableRow key={child.id} hover>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                    <Avatar src={child.photo_url || ''} />
                                                                    <Typography variant="body2">{child.full_name}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>{child.parent_name}</TableCell>
                                                            <TableCell>{child.joining_date ? new Date(child.joining_date).toLocaleDateString() : 'N/A'}</TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {child.recommended_therapies?.map(t => (
                                                                        <Typography key={t} variant="caption" sx={{ bgcolor: '#e0f7fa', px: 1, borderRadius: 1, color: '#006064' }}>
                                                                            {t.charAt(0).toUpperCase() + t.slice(1)}
                                                                        </Typography>
                                                                    )) || 'None'}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <Button component={Link} to={`/child-profile/${child.id}`} variant="outlined" size="small">
                                                                        Profile
                                                                    </Button>
                                                                    {userRole === 'admin' && (
                                                                        <IconButton color="error" size="small" onClick={() => handleDeleteChildClick(child)}>
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                        <TablePagination
                                            component="div"
                                            count={filteredChildren.length}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            rowsPerPageOptions={[5, 10, 25]}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Paper>

                        {/* Delete Child Dialog */}
                        <Dialog open={deleteChildOpen} onClose={() => setDeleteChildOpen(false)}>
                            <DialogTitle>Delete Child</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to delete <strong>{childToDelete?.full_name}</strong>? This will permanently delete all associated therapy sessions, assessments, and records. This action cannot be undone.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setDeleteChildOpen(false)}>Cancel</Button>
                                <Button color="error" onClick={confirmDeleteChild} variant="contained">Delete Child</Button>
                            </DialogActions>
                        </Dialog>
                    </>
                )}
                {tabValue === 2 && <ManageTherapists />}
            </Container>
        </Box>
    );
};

export default DoctorDashboard;